import React, { useState, useEffect } from 'react';
import { Table, Space, Tag, Button, Select, message, Spin } from 'antd';
import { ReloadOutlined, BookOutlined, RobotOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getSearchHistory } from '@/libs/api';
import type { SearchHistoryItem, SearchHistoryType } from '@/libs/types';
import { components } from '@/api/types';
import styles from '@/assets/styles/StudyCenter.module.less';

type UserInfo = components['schemas']['User'];

const { Option } = Select;

const sourceTypeColors = {
  ai: 'purple',
  dictionary: 'blue',
};

const sourceTypeIcons = {
  ai: <RobotOutlined />,
  dictionary: <BookOutlined />,
};

const WordHistory: React.FC<{ userInfo: UserInfo }> = ({ userInfo }) => {
  const [data, setData] = useState<SearchHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentType, setCurrentType] = useState<SearchHistoryType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchSearchHistory = async (type: SearchHistoryType = 'all', limit = 50) => {
    setLoading(true);
    try {
      const response = await getSearchHistory(type, limit, userInfo.userId);
      if (response.message === 'success') {
        setData(response.result.items);
        setTotal(response.result.total);
      } else {
        message.error('获取搜索历史失败');
      }
    } catch (error) {
      console.error('获取搜索历史失败:', error);
      message.error('获取搜索历史失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSearchHistory(currentType);
  }, [currentType]);

  const handleRefresh = () => {
    fetchSearchHistory(currentType);
  };

  const handleTypeChange = (value: SearchHistoryType) => {
    setCurrentType(value);
    setCurrentPage(1);
  };

  const getSourceType = (item: SearchHistoryItem): 'ai' | 'dictionary' => {
    return item.translation ? 'ai' : 'dictionary';
  };

  const getDisplayContent = (item: SearchHistoryItem): string => {
    if (item.translation) {
      // AI翻译结果：截取前100个字符
      const cleanTranslation = item.translation.replace(/<br>/g, ' ').replace(/<[^>]*>/g, '');
      return cleanTranslation.length > 100
        ? cleanTranslation.substring(0, 100) + '...'
        : cleanTranslation;
    } else if (item.word) {
      // 词典结果：显示基本释义
      const cleanMeaning = item.word.m.replace(/<br>/g, ' ').replace(/<[^>]*>/g, '');
      return cleanMeaning.length > 100 ? cleanMeaning.substring(0, 100) + '...' : cleanMeaning;
    }
    return '暂无释义';
  };

  const columns: ColumnsType<SearchHistoryItem> = [
    {
      title: '查询词',
      dataIndex: 'query',
      key: 'query',
      width: 150,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: '来源',
      key: 'source',
      width: 100,
      render: (_, record) => {
        const sourceType = getSourceType(record);
        return (
          <Tag color={sourceTypeColors[sourceType]} icon={sourceTypeIcons[sourceType]}>
            {sourceType === 'ai' ? 'AI翻译' : '词典'}
          </Tag>
        );
      },
    },
    {
      title: '内容',
      key: 'content',
      render: (_, record) => (
        <div style={{ maxWidth: 400, wordBreak: 'break-word' }}>{getDisplayContent(record)}</div>
      ),
    },
    {
      title: '查询次数',
      dataIndex: 'useCount',
      key: 'useCount',
      width: 100,
      sorter: (a, b) => a.useCount - b.useCount,
      render: (count: number) => (
        <Tag color={count > 3 ? 'red' : count > 1 ? 'orange' : 'default'}>{count}</Tag>
      ),
    },
    {
      title: '查询时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <section className={styles.container}>
      {
        /*
        TODO: 适配移动端
        */
      }
      <h2 className={styles.sectionTitle} style={{ textAlign: 'left', paddingLeft: 80 }}>
        查词记录
      </h2>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Select value={currentType} onChange={handleTypeChange} style={{ width: 120 }}>
              <Option value="all">全部</Option>
              <Option value="dictionary">词典查询</Option>
              <Option value="ai">AI翻译</Option>
            </Select>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>

        <Spin spinning={loading}>
          {/* TODO: 添加点击打开单词卡片功能 */}
          {/* TODO: 添加搜索功能 */}
          {/* TODO: 添加导出功能 */}
          {/* TODO: 添加删除功能 */}
          {/* TODO: 添加批量删除功能 */}
          {/* TODO: 添加批量导出功能 */}
          {/* TODO: 添加批量导入功能 */}
          {/* TODO: 添加批量删除功能 */}
          {/* TODO: 添加批量导出功能 */}
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
            scroll={{ x: 800 }}
          />
        </Spin>

        {data.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无查词记录</div>
        )}
      </Space>
    </section>
  );
};

export default WordHistory;
